import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Search, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import Pagination from '../../components/Pagination';

// ── Mock phone image placeholder ──────────────────────────────────────────────
const PHONE_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSI0IiBmaWxsPSIjRThFOEU4Ii8+PHJlY3QgeD0iMTIiIHk9IjYiIHdpZHRoPSIxNiIgaGVpZ2h0PSIyOCIgcng9IjMiIGZpbGw9IiM5OTkiLz48cmVjdCB4PSIxNCIgeT0iMTAiIHdpZHRoPSIxMiIgaGVpZ2h0PSIxOCIgcng9IjEiIGZpbGw9IiNDQ0MiLz48Y2lyY2xlIGN4PSIyMCIgY3k9IjMxIiByPSIxLjUiIGZpbGw9IiM3NzciLz48L3N2Zz4=';

// ── Sample data ───────────────────────────────────────────────────────────────
const BRANCHES = ['Bedok', 'Bishan', 'Chinatown', 'Bukit', 'Hougang', 'HQ', 'Tampines', 'Jurong'];

const generateMockData = () =>
    Array.from({ length: 800 }, (_, i) => ({
        id: i + 1,
        name: 'Samsung S25 Plus',
        variant: 'Silver Shadow, 256 GB',
        condition: 'New',
        grade: 'Unactivated',
        serialNo: `123456-78-901234-${i + 1}`,
        branch: BRANCHES[i % BRANCHES.length],
        costPrice: 1112000 + (i % 10) * 1000,
        currency: 'SGD',
        sellableQty: [1234, 1234.56, 1000000, 10, 999, 88888, 10000, 99][i % 8],
        lastStock: `2025-01-${String(23 - (i % 23)).padStart(2, '0')}`,
        linkedQty: 0,
    }));

const ALL_DATA = generateMockData();
const PAGE_SIZE = 7;

// ── FilterIcon ────────────────────────────────────────────────────────────────
const FilterIcon = ({ active }) =>
    active ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <g clipPath="url(#a)"><path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 8.55813 19.726 8.55813 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" /></g>
            <defs><clipPath id="a"><rect width="12" height="12" fill="white" transform="translate(11 5)" /></clipPath></defs>
        </svg>
    ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

// ── BranchTag ─────────────────────────────────────────────────────────────────
const BranchTag = ({ label, onRemove }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#FFEB00', borderRadius: 99,
        padding: '3px 10px', fontSize: 13, fontWeight: 600,
        color: '#212529', whiteSpace: 'nowrap',
    }}>
        {label}
        <button
            type="button"
            onClick={() => onRemove(label)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#212529' }}
        >
            <X size={12} strokeWidth={2.5} />
        </button>
    </span>
);

// ── LinkedQtyInput ────────────────────────────────────────────────────────────
const LinkedQtyInput = ({ value, onChange }) => (
    <div
        style={{
            display: 'inline-flex', alignItems: 'stretch',
            border: '1px solid #CED4DA', borderRadius: 6,
            overflow: 'hidden', width: 90, height: 34,
            background: '#E9ECEF',
        }}
        onClick={e => e.stopPropagation()}
    >
        {/* Number display */}
        <div style={{
            flex: 1, display: 'flex', alignItems: 'center',
            padding: '0 10px', fontSize: 13, color: '#212529',
            fontFamily: 'Montserrat', background: '#E9ECEF',
            userSelect: 'none',
        }}>
            {value}
        </div>

        {/* Up / Down buttons */}
        <div style={{
            display: 'flex', flexDirection: 'column',
            borderLeft: '1px solid #CED4DA', width: 24, flexShrink: 0,
        }}>
            {/* Up */}
            <button
                type="button"
                onClick={e => { e.stopPropagation(); onChange(value + 1); }}
                style={{
                    flex: 1, border: 'none', background: '#E9ECEF',
                    cursor: 'pointer', padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderBottom: '1px solid #CED4DA',
                    lineHeight: 0,
                }}
            >
                <svg width="9" height="6" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.5 4.5L4.5 1.5L7.5 4.5" stroke="#6C757D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            {/* Down */}
            <button
                type="button"
                onClick={e => { e.stopPropagation(); onChange(Math.max(0, value - 1)); }}
                style={{
                    flex: 1, border: 'none', background: '#E9ECEF',
                    cursor: 'pointer', padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 0,
                }}
            >
                <svg width="9" height="6" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.5 1.5L4.5 4.5L7.5 1.5" stroke="#6C757D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        </div>
    </div>
);

// ── Main Modal ────────────────────────────────────────────────────────────────
export default function LinkStockInstancesModal({ isOpen, onClose, onConfirm }) {
    // search
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // filter dropdown
    const [filterOpen, setFilterOpen] = useState(false);
    const filterRef = useRef(null);

    // filter state (temp while dropdown open)
    const [tempBranches, setTempBranches] = useState([]);
    const [tempCostMin, setTempCostMin] = useState('');
    const [tempCostMax, setTempCostMax] = useState('');

    // applied filters
    const [appliedBranches, setAppliedBranches] = useState([]);
    const [appliedCostMin, setAppliedCostMin] = useState('');
    const [appliedCostMax, setAppliedCostMax] = useState('');

    // branch dropdown inside filter
    const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
    const branchDropdownRef = useRef(null);

    // pagination
    const [currentPage, setCurrentPage] = useState(1);

    // linked quantities per row id
    const [linkedQtys, setLinkedQtys] = useState({});

    // ── close filter on outside click ──
    useEffect(() => {
        if (!filterOpen) return;
        const handler = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [filterOpen]);

    useEffect(() => {
        if (!branchDropdownOpen) return;
        const handler = (e) => {
            if (branchDropdownRef.current && !branchDropdownRef.current.contains(e.target)) {
                setBranchDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [branchDropdownOpen]);

    // ── filtered data ──
    const filtered = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return ALL_DATA.filter(row => {
            if (term && !`${row.name} ${row.variant} ${row.serialNo}`.toLowerCase().includes(term)) return false;
            if (appliedBranches.length && !appliedBranches.includes(row.branch)) return false;
            if (appliedCostMin !== '' && row.costPrice < Number(appliedCostMin)) return false;
            if (appliedCostMax !== '' && row.costPrice > Number(appliedCostMax)) return false;
            return true;
        });
    }, [searchTerm, appliedBranches, appliedCostMin, appliedCostMax]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const pageData = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const filtersApplied = appliedBranches.length > 0 || appliedCostMin !== '' || appliedCostMax !== '';

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            setSearchTerm(searchInput);
            setCurrentPage(1);
        }
    };

    const openFilter = () => {
        // sync temp with applied
        setTempBranches([...appliedBranches]);
        setTempCostMin(appliedCostMin);
        setTempCostMax(appliedCostMax);
        setFilterOpen(true);
    };

    const handleFilterConfirm = () => {
        setAppliedBranches([...tempBranches]);
        setAppliedCostMin(tempCostMin);
        setAppliedCostMax(tempCostMax);
        setCurrentPage(1);
        setFilterOpen(false);
    };

    const handleFilterClear = () => {
        setTempBranches([]);
        setTempCostMin('');
        setTempCostMax('');
    };

    const handleClearAll = () => {
        setAppliedBranches([]);
        setAppliedCostMin('');
        setAppliedCostMax('');
        setSearchTerm('');
        setSearchInput('');
        setCurrentPage(1);
    };

    const handleConfirm = () => {
        const selected = Object.entries(linkedQtys)
            .filter(([, qty]) => qty > 0)
            .map(([id, qty]) => ({ id: Number(id), qty }));
        onConfirm && onConfirm(selected);
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Overlay */}
            <div
                style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.45)',
                    zIndex: 1040,
                }}
                onClick={onClose}
            />

            {/* Modal */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 'min(1080px, 96vw)',
                    height: 'min(90vh, 860px)',
                    maxHeight: '90vh',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                    zIndex: 1050,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    fontFamily: 'Montserrat, sans-serif',
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* ── Header ── */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '20px 24px 12px',
                    flexShrink: 0,
                }}>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#212529' }}>
                        Select StockInstance
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: 4, display: 'flex', alignItems: 'center', color: '#212529',
                        }}
                    >
                        <X size={22} strokeWidth={2} />
                    </button>
                </div>

                {/* ── Toolbar ── */}
                <div style={{ padding: '0 24px 12px', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
                    {/* Search */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        border: '1px solid #CED4DA', borderRadius: 8,
                        padding: '0 12px', height: 40, width: 260, background: '#fff',
                    }}>
                        <Search size={16} color="#6C757D" />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            onBlur={() => { setSearchTerm(searchInput); setCurrentPage(1); }}
                            style={{
                                border: 'none', outline: 'none', flex: 1,
                                fontSize: 13, fontFamily: 'Montserrat',
                                background: 'transparent', color: '#212529',
                            }}
                        />
                    </div>

                    {/* Filter button + dropdown */}
                    <div style={{ position: 'relative' }} ref={filterRef}>
                        <button
                            type="button"
                            onClick={openFilter}
                            style={{
                                width: 40, height: 40, border: `1px solid ${filtersApplied ? '#0D6EFD' : '#6C757D'}`,
                                borderRadius: 8, background: filtersApplied ? '#CFE2FF' : '#fff',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <FilterIcon active={filtersApplied} />
                        </button>

                        {/* Filter Dropdown */}
                        {filterOpen && (
                            <div style={{
                                position: 'absolute', top: 44, left: 0, zIndex: 200,
                                background: '#fff', borderRadius: 10,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                padding: '20px 20px 16px',
                                width: 380,
                                fontFamily: 'Montserrat',
                            }}>
                                {/* Branch */}
                                <div style={{ marginBottom: 18 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 8 }}>Branch</div>

                                    {/* Tag box */}
                                    <div style={{
                                        border: '1px solid #CED4DA', borderRadius: 8,
                                        padding: '6px 8px', minHeight: 44,
                                        display: 'flex', flexWrap: 'wrap', gap: 6,
                                        alignItems: 'center', position: 'relative',
                                        cursor: 'pointer', background: '#fff',
                                    }}
                                        onClick={() => setBranchDropdownOpen(v => !v)}
                                    >
                                        {tempBranches.length === 0 && (
                                            <span style={{ color: '#ADB5BD', fontSize: 13 }}>Select branches...</span>
                                        )}
                                        {tempBranches.map(b => (
                                            <BranchTag key={b} label={b} onRemove={(br) => {
                                                setTempBranches(prev => prev.filter(x => x !== br));
                                            }} />
                                        ))}
                                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            {tempBranches.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={e => { e.stopPropagation(); setTempBranches([]); }}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                                                >
                                                    <X size={14} color="#6C757D" />
                                                </button>
                                            )}
                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 5L7 9L11 5" stroke="#6C757D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </div>

                                        {/* Branch list dropdown */}
                                        {branchDropdownOpen && (
                                            <div
                                                ref={branchDropdownRef}
                                                style={{
                                                    position: 'absolute', top: '100%', left: 0, right: 0,
                                                    background: '#fff', border: '1px solid #CED4DA',
                                                    borderRadius: 8, zIndex: 300, marginTop: 4,
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                    maxHeight: 200, overflowY: 'auto',
                                                }}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                {BRANCHES.map(b => (
                                                    <label key={b} style={{
                                                        display: 'flex', alignItems: 'center', gap: 10,
                                                        padding: '8px 12px', cursor: 'pointer',
                                                        fontSize: 13, color: '#212529',
                                                    }}>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            style={{ margin: 0, cursor: 'pointer', backgroundColor: '#fff', borderColor: '#ADB5BD' }}
                                                            checked={tempBranches.includes(b)}
                                                            onChange={e => {
                                                                if (e.target.checked) setTempBranches(p => [...p, b]);
                                                                else setTempBranches(p => p.filter(x => x !== b));
                                                            }}
                                                        />
                                                        {b}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Cost Price */}
                                <div style={{ marginBottom: 20 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 8 }}>Cost Price</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        {/* Min */}
                                        <div style={{
                                            display: 'flex', alignItems: 'center',
                                            border: '1px solid #CED4DA', borderRadius: 8,
                                            overflow: 'hidden', flex: 1, height: 38,
                                        }}>
                                            <span style={{
                                                padding: '0 10px', fontSize: 12, color: '#6C757D',
                                                background: '#F3F4F6', borderRight: '1px solid #CED4DA',
                                                height: '100%', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
                                            }}>SGD</span>
                                            <input
                                                type="number"
                                                placeholder="1,900.00"
                                                value={tempCostMin}
                                                onChange={e => setTempCostMin(e.target.value)}
                                                style={{
                                                    flex: 1, border: 'none', outline: 'none',
                                                    padding: '0 8px', fontSize: 13,
                                                    fontFamily: 'Montserrat', background: 'transparent',
                                                }}
                                            />
                                        </div>
                                        <span style={{ color: '#6C757D', fontSize: 14 }}>-</span>
                                        {/* Max */}
                                        <div style={{
                                            display: 'flex', alignItems: 'center',
                                            border: '1px solid #CED4DA', borderRadius: 8,
                                            overflow: 'hidden', flex: 1, height: 38,
                                        }}>
                                            <span style={{
                                                padding: '0 10px', fontSize: 12, color: '#6C757D',
                                                background: '#F3F4F6', borderRight: '1px solid #CED4DA',
                                                height: '100%', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
                                            }}>SGD</span>
                                            <input
                                                type="number"
                                                placeholder="2,000.00"
                                                value={tempCostMax}
                                                onChange={e => setTempCostMax(e.target.value)}
                                                style={{
                                                    flex: 1, border: 'none', outline: 'none',
                                                    padding: '0 8px', fontSize: 13,
                                                    fontFamily: 'Montserrat', background: 'transparent',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <button
                                        type="button"
                                        onClick={handleFilterClear}
                                        style={{
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            fontSize: 13, color: '#212529', fontFamily: 'Montserrat',
                                            fontWeight: 500, padding: '6px 0',
                                        }}
                                    >
                                        Clear
                                    </button>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <button
                                            type="button"
                                            onClick={() => setFilterOpen(false)}
                                            style={{
                                                height: 36, padding: '0 20px', borderRadius: 8,
                                                border: '1px solid #CED4DA', background: '#fff',
                                                cursor: 'pointer', fontSize: 13, fontFamily: 'Montserrat',
                                                fontWeight: 500, color: '#212529',
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleFilterConfirm}
                                            style={{
                                                height: 36, padding: '0 20px', borderRadius: 8,
                                                border: 'none', background: '#212529',
                                                cursor: 'pointer', fontSize: 13, fontFamily: 'Montserrat',
                                                fontWeight: 600, color: '#fff',
                                            }}
                                        >
                                            Confirm
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Table ── */}
                <style>{`
                    .lsi-table .lsi-sticky-col {
                        position: sticky !important;
                        right: 0;
                        z-index: 2;
                    }
                    .lsi-table thead th {
                        position: sticky;
                        top: 0;
                        background: #F8F9FA;
                        z-index: 3;
                    }
                    .lsi-table thead .lsi-sticky-col {
                        background: #F8F9FA;
                        z-index: 5;
                    }
                    .lsi-table tbody tr:nth-child(odd) .lsi-sticky-col {
                        background: #fff;
                    }
                    .lsi-table tbody tr:nth-child(even) .lsi-sticky-col {
                        background: #F8F9FA;
                    }
                    .lsi-table .lsi-sticky-col::before {
                        content: "";
                        position: absolute;
                        left: -8px;
                        top: 0;
                        width: 8px;
                        height: 100%;
                        background: linear-gradient(to left, rgba(0,0,0,0.08), rgba(0,0,0,0));
                        pointer-events: none;
                    }
                `}</style>
                <div style={{ flex: 1, overflowX: 'scroll', overflowY: 'auto', position: 'relative', minHeight: 0 }}>
                    <table className="lsi-table" style={{
                        borderCollapse: 'collapse',
                        fontFamily: 'Montserrat', fontSize: 13,
                        tableLayout: 'auto',
                        width: 'max-content',
                        minWidth: '100%',
                    }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 4 }}>
                            <tr style={{ background: '#F8F9FA', borderBottom: '1px solid #E9ECEF' }}>
                                {[
                                    { label: 'Select',      width: 56  },
                                    { label: 'Stock',       width: 220 },
                                    { label: 'Serial No.',  width: 160 },
                                    { label: 'Branch',      width: 100 },
                                    { label: 'Cost Price',  width: 150 },
                                    { label: 'Sellable Qty',width: 120 },
                                    { label: 'Last Stock',  width: 110 },
                                    { label: 'Linked Qty',  width: 110 },
                                ].map((col) => (
                                    <th key={col.label}
                                        className={col.label === 'Linked Qty' ? 'lsi-sticky-col' : ''}
                                        style={{
                                            padding: '12px 14px', fontWeight: 600, fontSize: 13,
                                            color: '#212529', whiteSpace: 'nowrap', textAlign: 'left',
                                            minWidth: col.width,
                                        }}>
                                        {col.label === 'Sellable Qty' ? (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                                {col.label} <ChevronsUpDown size={14} color="#6C757D" />
                                            </span>
                                        ) : col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageData.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#6C757D' }}>
                                        No results found
                                    </td>
                                </tr>
                            ) : pageData.map((row, idx) => (
                                <tr key={row.id} style={{
                                    background: idx % 2 === 1 ? '#F8F9FA' : '#fff',
                                    borderBottom: '1px solid #F0F0F0',
                                }}>
                                    {/* Checkbox */}
                                    <td style={{ padding: '10px 14px', width: 48 }}>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            style={{ margin: 0, cursor: 'pointer', backgroundColor: '#fff', borderColor: '#ADB5BD' }}
                                            checked={(linkedQtys[row.id] || 0) > 0}
                                            onChange={e => {
                                                const checked = e.target.checked;
                                                setLinkedQtys(prev => ({
                                                    ...prev,
                                                    [row.id]: checked ? (prev[row.id] || 1) : 0,
                                                }));
                                            }}
                                        />
                                    </td>

                                    {/* Stock */}
                                    <td style={{ padding: '10px 14px', minWidth: 180 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <img
                                                src={PHONE_PLACEHOLDER}
                                                alt="product"
                                                style={{ width: 36, height: 36, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
                                            />
                                            <div>
                                                <div style={{ fontWeight: 500, color: '#212529', fontSize: 13 }}>{row.name}</div>
                                                <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>{row.variant}</div>
                                                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                                    <span style={{ background: '#FFEB00', borderRadius: 99, padding: '1px 7px', fontSize: 10, fontWeight: 600, color: '#212529' }}>
                                                        {row.condition}
                                                    </span>
                                                    <span style={{ background: '#FFEB00', borderRadius: 99, padding: '1px 7px', fontSize: 10, fontWeight: 600, color: '#212529' }}>
                                                        {row.grade}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>

                                    {/* Serial No */}
                                    <td style={{ padding: '10px 14px', color: '#212529', whiteSpace: 'nowrap' }}>
                                        {row.serialNo}
                                    </td>

                                    {/* Branch */}
                                    <td style={{ padding: '10px 14px', color: '#212529' }}>
                                        {row.branch}
                                    </td>

                                    {/* Cost Price */}
                                    <td style={{ padding: '10px 14px', color: '#212529', whiteSpace: 'nowrap' }}>
                                        {row.currency} {row.costPrice.toLocaleString('en-SG', { minimumFractionDigits: 2 })}
                                    </td>

                                    {/* Sellable Qty */}
                                    <td style={{ padding: '10px 14px', color: '#212529' }}>
                                        {typeof row.sellableQty === 'number'
                                            ? row.sellableQty.toLocaleString()
                                            : row.sellableQty}
                                    </td>

                                    {/* Last Stock */}
                                    <td style={{ padding: '10px 14px', color: '#212529', whiteSpace: 'nowrap' }}>
                                        {row.lastStock}
                                    </td>

                                    {/* Linked Qty — sticky right */}
                                    <td className="lsi-sticky-col" style={{ padding: '10px 14px' }}>
                                        <LinkedQtyInput
                                            value={linkedQtys[row.id] ?? 0}
                                            onChange={val => setLinkedQtys(prev => ({ ...prev, [row.id]: val }))}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* ── Pagination ── */}
                <div style={{
                    padding: '12px 24px',
                    borderTop: '1px solid #E9ECEF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 12,
                    flexShrink: 0,
                    flexWrap: 'wrap',
                }}>
                    {/* Info */}
                    <span style={{ fontSize: 13, color: '#6C757D' }}>
                        {filtered.length === 0
                            ? '0 of 0'
                            : `${(safePage - 1) * PAGE_SIZE + 1} - ${Math.min(safePage * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
                    </span>

                    <InlinePagination
                        currentPage={safePage}
                        totalPages={totalPages}
                        onPageChange={(p) => setCurrentPage(p)}
                    />
                </div>

                {/* ── Footer ── */}
                <div style={{
                    padding: '14px 24px',
                    borderTop: '1px solid #E9ECEF',
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    gap: 12, flexShrink: 0,
                }}>
                    <button
                        type="button"
                        onClick={handleClearAll}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: 13, color: '#212529', fontFamily: 'Montserrat',
                            fontWeight: 500, padding: '6px 12px',
                        }}
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            height: 38, padding: '0 24px', borderRadius: 8,
                            border: '1px solid #212529', background: '#fff',
                            cursor: 'pointer', fontSize: 13, fontFamily: 'Montserrat',
                            fontWeight: 600, color: '#212529',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        style={{
                            height: 38, padding: '0 24px', borderRadius: 8,
                            border: 'none', background: '#212529',
                            cursor: 'pointer', fontSize: 13, fontFamily: 'Montserrat',
                            fontWeight: 600, color: '#fff',
                        }}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </>
    );
}

// ── Inline Pagination (mirrors the existing Pagination component pattern) ─────
function InlinePagination({ currentPage, totalPages, onPageChange }) {
    if (totalPages <= 1) return null;

    const getPages = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }
        pages.push(1);
        if (currentPage > 4) pages.push('...');
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        if (currentPage < totalPages - 3) pages.push('...');
        pages.push(totalPages);
        return pages;
    };

    const btnStyle = (active) => ({
        width: 34, height: 34, borderRadius: 8,
        border: active ? 'none' : '1px solid #DEE2E6',
        background: active ? '#FFEB00' : '#fff',
        color: '#212529',
        cursor: 'pointer', fontSize: 13,
        fontFamily: 'Montserrat', fontWeight: active ? 700 : 400,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    });

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
                style={{ ...btnStyle(false), opacity: currentPage === 1 ? 0.4 : 1 }}
            >
                <ChevronLeft size={16} />
            </button>

            {getPages().map((p, i) =>
                p === '...' ? (
                    <span key={`ellipsis-${i}`} style={{ width: 34, textAlign: 'center', fontSize: 13, color: '#6C757D' }}>…</span>
                ) : (
                    <button
                        key={p}
                        type="button"
                        onClick={() => onPageChange(p)}
                        style={btnStyle(p === currentPage)}
                    >
                        {p}
                    </button>
                )
            )}

            <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
                style={{ ...btnStyle(false), opacity: currentPage === totalPages ? 0.4 : 1 }}
            >
                <ChevronRight size={16} />
            </button>
        </div>
    );
}
